import { NgModule } from "@angular/core";
import { RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { NgxDatatableModule } from "@swimlane/ngx-datatable";
import { NgSelectModule } from "@ng-select/ng-select";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { QuillModule } from "ngx-quill";
import { CardSnippetModule } from "@core/components/card-snippet/card-snippet.module";
import { FormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";

import * as hljs from "highlight.js";
import { CoreCommonModule } from "@core/common.module";
import { FileUploadModule } from "ng2-file-upload";

import { ContentHeaderModule } from "app/layout/components/content-header/content-header.module";

import { SampleComponent } from "./sample.component";
import { HomeComponent } from "./home.component";
import { EventsComponent } from "./events/events.component";
import { OrganizersComponent } from "./organizers/organizers.component";
import { UsersComponent } from "./users/users.component";
import { OrgOrdersComponent } from "./org-orders/org-orders.component";
import { EventCategoriesComponent } from "./event-categories/event-categories.component";
import { NotificationsComponent } from "./notifications/notifications.component";
import { OffersComponent } from "./offers/offers.component";
import { NgApexchartsModule } from "ng-apexcharts";
import { TabComponent } from "../components/tab/tab.component";
import { TabsComponent } from "../components/tabs/tabs.component";
import { EventsActionComponent } from "./events-action/events-action.component";
import { OrganizersActionComponent } from "./organizers-action/organizers-action.component";
import { AdminsComponent } from "./admins/admins.component";
import { NgbToastModule } from "@ng-bootstrap/ng-bootstrap";
import { AuthGuard } from "app/auth/helpers";
import { AuthLoginV2Component } from "../pages/authentication/auth-login-v2/auth-login-v2.component";
import { AssignEventComponent } from "./assign-event/assign-event.component";
import { OrganizerReportComponent } from "./organizer-report/organizer-report.component";
import { OrganizerEventScheduleCheckInAndCheckOutComponent } from "./organizer-event-schedule-check-in-and-check-out/organizer-event-schedule-check-in-and-check-out.component";
import { ScanLogsComponent } from "./scan-logs/scan-logs.component";
import { UserCartItemComponent } from "./user-cart-item/user-cart-item.component";

const routes = [
  {
    path: "login",
    component: AuthLoginV2Component,
    data: { animation: "auth" },
  },
  {
    path: "sample",
    component: SampleComponent,
    data: { animation: "sample" },
    canActivate: [AuthGuard],
  },
  {
    path: "home",
    component: HomeComponent,
    data: { animation: "home", roles: ["Admin", "Assistant", "Organizer"] },
    canActivate: [AuthGuard],
  },
  {
    path: "events",
    component: EventsComponent,
    data: { animation: "events", roles: ["Admin", "Assistant", "Organizer"] },
    canActivate: [AuthGuard],
  },
  {
    path: "organizers",
    component: OrganizersComponent,
    data: { animation: "organizers", roles: ["Admin", "Assistant"] },
    canActivate: [AuthGuard],
  },
  {
    path: "users",
    component: UsersComponent,
    data: { animation: "users", roles: ["Admin"] },
    canActivate: [AuthGuard],
  },
  {
    path: "org-orders",
    component: OrgOrdersComponent,
    data: { animation: "org-orders", roles: ["Admin"] },
    canActivate: [AuthGuard],
  },
  {
    path: "event-categories",
    component: EventCategoriesComponent,
    data: { animation: "event-categories", roles: ["Admin"] },
    canActivate: [AuthGuard],
  },
  {
    path: "organizers-check-in-and-out",
    component: OrganizerEventScheduleCheckInAndCheckOutComponent,
    data: { animation: "event-categories", roles: ["Admin"] },
    canActivate: [AuthGuard],
  },
  {
    path: "scan-logs",
    component: ScanLogsComponent,
    data: { animation: "event-categories", roles: ["Admin"] },
    canActivate: [AuthGuard],
  },
  {
    path: "user-cart-items",
    component: UserCartItemComponent,
    data: { animation: "event-categories", roles: ["Admin"] },
    canActivate: [AuthGuard],
  },

  // {
  //   path: 'notifications',
  //   component: NotificationsComponent,
  //   data: { animation: 'notifications' },
  //   canActivate: [AuthGuard]
  // },
  {
    path: "offers",
    component: OffersComponent,
    data: { animation: "offers", roles: ["Admin"] },
    canActivate: [AuthGuard],
  },
  {
    path: "new-event",
    component: EventsActionComponent,
    data: { animation: "new-event", roles: ["Admin", "Assistant", "Organizer"] },
    canActivate: [AuthGuard],
  },
  {
    path: "assign-event",
    component: AssignEventComponent,
    data: { animation: "new-event", roles: ["Admin", "Assistant"] },
    canActivate: [AuthGuard],
  },
  {
    path: "assign-event/:id",
    component: AssignEventComponent,
    data: { animation: "new-event", roles: ["Admin", "Assistant"] },
    canActivate: [AuthGuard],
  },
  {
    path: "edit-event/:id",
    component: EventsActionComponent,
    data: { animation: "edit-event", roles: ["Admin", "Assistant", "Organizer"] },
    canActivate: [AuthGuard],
  },
  {
    path: "new-org",
    component: OrganizersActionComponent,
    data: { animation: "new-org", roles: ["Admin", "Assistant"] },
    canActivate: [AuthGuard],
  },
  {
    path: "edit-org/:id",
    component: OrganizersActionComponent,
    data: { animation: "edit-org", roles: ["Admin", "Assistant"] },
    canActivate: [AuthGuard],
  },
  {
    path: "admins",
    component: AdminsComponent,
    data: { animation: "admins", roles: ["Admin"] },
    canActivate: [AuthGuard],
  },
  {
    path: "organizers-reports",
    component: OrganizerReportComponent,
    data: { animation: "admins", roles: ["Admin"] },
    canActivate: [AuthGuard],
  },
];

@NgModule({
  declarations: [
    SampleComponent,
    HomeComponent,
    EventsComponent,
    OrganizersComponent,
    UsersComponent,
    OrgOrdersComponent,
    EventCategoriesComponent,
    NotificationsComponent,
    OffersComponent,
    TabComponent,
    TabsComponent,
    EventsActionComponent,
    OrganizersActionComponent,
    AdminsComponent,
    AssignEventComponent,
    OrganizerReportComponent,
    OrganizerEventScheduleCheckInAndCheckOutComponent,
    ScanLogsComponent,
    UserCartItemComponent,
  ],
  imports: [
    RouterModule.forChild(routes),
    ContentHeaderModule,
    TranslateModule,
    CoreCommonModule,
    NgApexchartsModule,
    NgxDatatableModule,
    NgSelectModule,
    FileUploadModule,
    NgbModule,
    CardSnippetModule,
    FormsModule,
    BrowserModule,
    QuillModule.forRoot({
      modules: {
        syntax: true, // Enable syntax highlighting
        toolbar: [
          ["bold", "italic", "underline", "strike"], // Add toolbar options
          ["code-block"], // Add code block button
        ],
      },
    }),
    NgbToastModule,
  ],
  exports: [SampleComponent, HomeComponent],
})
export class SampleModule {}
